import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	type Money,
	moneyFromMajorFloat,
	percentOf,
	toMajorFloatAmount,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";
import type { TableRef } from "@performyard/py-components";
import { Table, Text } from "@performyard/py-components";
import { toFixed } from "@performyard/utils-isomorphic";

import type { EmployeeValue } from "../../../../../components/Inputs/SelectEmployee";
import { noop } from "./ExpandedDecisionTable";

import type { DecisionRow } from "../../../types";
import type { GroupBudgetFigures } from "../../MyDecisions/types";
import { DecisionTableContext, IncreaseValues } from "../../MyDecisions/types";

import type { ManagerBudgetGroupWithCycleAllotment } from "../../SharedComponents/utils";
import {
	budgetGroupKey,
	computeAdjustedBudgetFigures,
	mapBudgetGroupResultsToDecisionRows,
} from "../../SharedComponents/utils";

import { createDecisionStore } from "../../MyDecisions/decision-store";
import type { DecisionStore } from "../../MyDecisions/decision-store";
import { buildAuditLogEntries } from "../../SharedComponents/AuditLog/buildAuditLogEntries";
import { AuditLogModal } from "../../SharedComponents/AuditLogModal";
import { BudgetSummaryCard } from "../../SharedComponents/BudgetSummaryCard";
import {
	BUDGET_REVIEW_TABLE_CLASSNAME,
	budgetReviewManagerGroupStatusShowsRowEdit,
	budgetReviewReadOnlySalaryColumnDefs,
	budgetReviewSalaryColumnDefsWithEdit,
} from "../column-defs";

type BudgetReviewExpandedDecisionTableProps = {
	budget: ManagerBudgetGroupWithCycleAllotment;
	figures: GroupBudgetFigures;
	compensationCycleId: string;
	cycleStatus: string;
	salaryFieldId?: string;
	budgetReviewInfoLinksReadOnly?: boolean;
	canAlwaysViewSalary?: boolean;
};

export const BudgetReviewExpandedDecisionTable: React.FC<BudgetReviewExpandedDecisionTableProps> = ({
	budget,
	figures,
	compensationCycleId,
	cycleStatus,
	salaryFieldId,
	budgetReviewInfoLinksReadOnly,
	canAlwaysViewSalary,
}) => {
	const [auditLogManagerId, setAuditLogManagerId] = useState<string | null>(null);
	const [editingRowIds, setEditingRowIds] = useState<Set<string>>(new Set());
	const [draftOverrides, setDraftOverrides] = useState<IncreaseValues>({});
	const gridRef = useRef<TableRef<DecisionRow> | null>(null);
	const baseRows = useMemo(() => mapBudgetGroupResultsToDecisionRows(budget.results), [budget.results]);

	const salaryById = useMemo(() => {
		const map = new Map<string, Money>();
		for (const row of baseRows) {
			map.set(row._id, row.current_salary);
		}
		return map;
	}, [baseRows]);

	const onBudgetReviewEditRow = useCallback((row: DecisionRow) => {
		setEditingRowIds((prev) => {
			const next = new Set(prev);
			next.add(row._id);
			return next;
		});
	}, []);

	const onBudgetReviewCancelEditRow = useCallback((row: DecisionRow) => {
		setEditingRowIds((prev) => {
			const next = new Set(prev);
			next.delete(row._id);
			return next;
		});
		setDraftOverrides((prev) => {
			const { [row._id]: _removed, ...rest } = prev;
			return rest;
		});
	}, []);

	const onPercentChange = useCallback(
		(id: string, percent: number | "") => {
			setDraftOverrides((prev) => {
				if (percent === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? zeroMoney();
				// Matches `useIncreaseValues.onPercentChange`. This was the last copy of the
				// `Math.round(x*100)/100` band-aid, and unlike a display rounding its result
				// is persisted by the cycle-owner edit.
				const amount = toMajorFloatAmount(percentOf(salary, percent));
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const onAmountChange = useCallback(
		(id: string, amount: number | "") => {
			setDraftOverrides((prev) => {
				if (amount === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? zeroMoney();
				// Ratio over exact minor units on both sides, and the zero guard is on the
				// minor units rather than on a float. Matches `useIncreaseValues`.
				const salaryMinorUnits = toMinorUnits(salary);
				const percent =
					salaryMinorUnits > 0
						? Math.round((toMinorUnits(moneyFromMajorFloat(amount)) / salaryMinorUnits) * 10000) / 100
						: 0;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const getActorNameForRow = useCallback(
		(row: DecisionRow): string | null => {
			const approverId = row.approver;
			if (!approverId) return null;
			if (budget.overrideManagerId != null && approverId === budget.overrideManagerId) {
				return budget.overrideManagerName?.trim() || null;
			}
			if (approverId === budget.managerId) {
				return budget.managerName?.trim() || null;
			}
			return null;
		},
		[budget.managerId, budget.managerName, budget.overrideManagerId, budget.overrideManagerName]
	);

	const budgetGroupAuditKey = useMemo(() => budgetGroupKey(budget), [budget]);

	const assignedActingManagerForModal = useMemo((): EmployeeValue | undefined => {
		if (budget.overrideManagerId == null) {
			return undefined;
		}
		return {
			id: budget.overrideManagerId,
			name: budget.overrideManagerName?.trim() ?? "",
		};
	}, [budget.overrideManagerId, budget.overrideManagerName]);

	const getActorNameById = useCallback(
		(id: string): string | null => {
			if (id === budget.managerId) return budget.managerName?.trim() || null;
			if (budget.overrideManagerId != null && id === budget.overrideManagerId) {
				return budget.overrideManagerName?.trim() || null;
			}
			return null;
		},
		[budget.managerId, budget.managerName, budget.overrideManagerId, budget.overrideManagerName]
	);

	const auditLogEntries = useMemo(
		() => buildAuditLogEntries(budget.managerId, baseRows, getActorNameForRow, getActorNameById),
		[budget.managerId, baseRows, getActorNameForRow, getActorNameById]
	);
	const columnDefs = useMemo(
		() =>
			budgetReviewManagerGroupStatusShowsRowEdit(budget.status, cycleStatus)
				? budgetReviewSalaryColumnDefsWithEdit
				: budgetReviewReadOnlySalaryColumnDefs,
		[budget.status, cycleStatus]
	);

	const increaseValues: IncreaseValues = useMemo(() => {
		const merged: IncreaseValues = {};
		for (const row of baseRows) {
			if (draftOverrides[row._id]) {
				merged[row._id] = draftOverrides[row._id];
			} else {
				merged[row._id] = {
					percent: row.proposed_increase_percent != null ? toFixed(row.proposed_increase_percent) : null,
					// Money becomes a number only here, at the numeric input boundary.
					amount:
						row.proposed_increase_amount != null ? toMajorFloatAmount(row.proposed_increase_amount) : null,
				};
			}
		}
		return merged;
	}, [baseRows, draftOverrides]);

	const adjustedFigures: GroupBudgetFigures = useMemo(
		() => computeAdjustedBudgetFigures(baseRows, draftOverrides, figures.groupIncreaseBudget),
		[figures, baseRows, draftOverrides]
	);

	const recommendedIncreasePercent = useMemo(
		() =>
			typeof budget.allotmentIncreasePercent === "number" && Number.isFinite(budget.allotmentIncreasePercent)
				? toFixed(budget.allotmentIncreasePercent)
				: 0,
		[budget.allotmentIncreasePercent]
	);

	const notesValuesRef = useRef<Record<string, string>>({});

	const storeRef = useRef<DecisionStore | null>(null);
	if (storeRef.current === null) {
		storeRef.current = createDecisionStore({ increaseValues, editingRowIds });
	}
	const store = storeRef.current;

	// Sync React state into the store so cell renderers re-render via useSyncExternalStore
	useEffect(() => {
		store.setState({ increaseValues, editingRowIds });
	}, [store, increaseValues, editingRowIds]);

	// increaseValues and editingRowIds are intentionally omitted from deps —
	// cell renderers read live values from the store via useSyncExternalStore,
	// so the context object doesn't need to change on every keystroke.
	const increaseValuesRef = useRef(increaseValues);
	increaseValuesRef.current = increaseValues;
	const editingRowIdsRef = useRef(editingRowIds);
	editingRowIdsRef.current = editingRowIds;

	const gridContext: DecisionTableContext = useMemo(
		() => ({
			store,
			get increaseValues() {
				return increaseValuesRef.current;
			},
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			notesValuesRef,
			onNotesChange: noop,
			groupCannotBeSubmitted: null,
			managerId: budget.managerId,
			expandedRowId: null,
			onExpand: noop,
			onCollapse: noop,
			readOnlyBudgetReview: true,
			get editingRowIds() {
				return editingRowIdsRef.current;
			},
			...(budgetReviewManagerGroupStatusShowsRowEdit(budget.status, cycleStatus)
				? { onBudgetReviewEditRow, onBudgetReviewCancelEditRow }
				: {}),
			salaryFieldId,
			budgetReviewInfoLinksReadOnly,
			canAlwaysViewSalary,
		}),
		[
			store,
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			budget.managerId,
			budget.status,
			onBudgetReviewEditRow,
			onBudgetReviewCancelEditRow,
			salaryFieldId,
			budgetReviewInfoLinksReadOnly,
			canAlwaysViewSalary,
		]
	);

	// Once the server data catches up to the draft, clear the draft and exit edit mode.
	// Only trigger on baseRows changes (server response), NOT on draftOverrides changes —
	// otherwise intermediate keystrokes that round to the same percent as the server
	// value would prematurely cancel editing and destroy the focused input.
	const draftOverridesRef = useRef(draftOverrides);
	draftOverridesRef.current = draftOverrides;
	useEffect(() => {
		const currentDrafts = draftOverridesRef.current;
		const staleIds: string[] = [];
		for (const id of editingRowIds) {
			const draft = currentDrafts[id];
			if (!draft) continue;
			const row = baseRows.find((r) => r._id === id);
			if (!row) continue;
			if (toFixed(row.proposed_increase_percent ?? 0, 2) === toFixed(draft.percent || 0, 2)) {
				staleIds.push(id);
			}
		}
		if (staleIds.length > 0) {
			setEditingRowIds((prev) => {
				const next = new Set(prev);
				for (const id of staleIds) next.delete(id);
				return next;
			});
			setDraftOverrides((prev) => {
				const next = { ...prev };
				for (const id of staleIds) delete next[id];
				return next;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [baseRows]);

	// The store handles increaseValues/editingRowIds updates via useSyncExternalStore,
	// so we no longer need refreshCells for those. Only refresh for context changes
	// that aren't covered by the store (e.g. column defs, budget status).
	useEffect(() => {
		const api = gridRef.current?.api;
		if (!api) return;
		api.refreshCells({ force: true });
	}, [recommendedIncreasePercent]);

	if (baseRows.length === 0) {
		return (
			<div className="border-t border-solid border-neutral-2 bg-neutral-0 py-4">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					No employees in this budget group.
				</Text>
			</div>
		);
	}

	return (
		<div className={BUDGET_REVIEW_TABLE_CLASSNAME}>
			<div className="pt-4">
				<BudgetSummaryCard
					figures={adjustedFigures}
					onViewAuditLog={() => setAuditLogManagerId(budgetGroupAuditKey)}
					page="budget-reviews"
					budgetManagerForActingManagerPicker={{ id: budget.managerId, name: budget.managerName }}
					compensationCycleId={compensationCycleId}
					budgetStatus={budget.status}
					assignedActingManager={assignedActingManagerForModal}
					cycleStatus={cycleStatus}
				/>
			</div>
			<div className="pt-[8px]">
				<Table<DecisionRow>
					ref={gridRef}
					rowData={baseRows}
					columnDefs={columnDefs}
					context={gridContext}
					autoHeight
					columnSizing="fit-grid"
					animateRows={true}
					getRowId={(params) => params.data._id}
					rowHeight={48}
					headerHeight={48}
				/>
			</div>

			{auditLogManagerId === budgetGroupAuditKey && (
				<AuditLogModal onClose={() => setAuditLogManagerId(null)} entries={auditLogEntries} />
			)}
		</div>
	);
};
